import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { PasswordTurnVisibleIcon, PasswordTurnInvisibleIcon } from '../../../Constants/Icons';

const AccountForm = ({
  setAccountDetailsParent,
  setSection,
  createNewUser,
  formTextboxLabel,
  orangeButton,
}) => {
  // State for account details
  const [accountDetails, setAccountDetails] = useState({
    contactName: '',
    title: '',
    email: '',
    phoneNumber: '',
    newPassword: '',
    confirmNewPassword: '',
    interestedInAPI: false,
    agreeToTerms: false,
  });

  // State for form errors
  const [formErrors, setFormErrors] = useState({});

  // State for password visibility
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  // State for password validation
  const [passwordValidation, setPasswordValidation] = useState({
    hasNumber: false,
    hasSpecialChar: false,
    hasUppercase: false,
    hasLowercase: false,
    longEnough: false,
    isPasswordMatch: false,
  });

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAccountDetails({
      ...accountDetails,
      [name]: type === 'checkbox' ? checked : value,
    });
    validateField(name, type === 'checkbox' ? checked : value);
  };

  // Handle phone number change
  const handlePhoneNumberChange = (value) => {
    setAccountDetails({
      ...accountDetails,
      phoneNumber: value,
    });
    validateField('phoneNumber', value);
  };

  // Toggle password visibility
  const toggleNewPasswordVisibility = () => {
    setIsNewPasswordVisible(!isNewPasswordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setIsConfirmPasswordVisible(!isConfirmPasswordVisible);
  };

  // Handle password change
  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setAccountDetails({
      ...accountDetails,
      newPassword: value,
    });
    validatePassword(value, accountDetails.confirmNewPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setAccountDetails({
      ...accountDetails,
      confirmNewPassword: value,
    });
    validatePassword(accountDetails.newPassword, value);
  };

  // Validate password
  const validatePassword = (password, confirmPassword) => {
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const longEnough = password.length >= 8;
    const isPasswordMatch = password === confirmPassword;

    setPasswordValidation({
      hasNumber,
      hasSpecialChar,
      hasUppercase,
      hasLowercase,
      longEnough,
      isPasswordMatch,
    });
  };

  // Validate individual fields
  const validateField = (name, value) => {
    let errors = { ...formErrors };

    switch (name) {
      case 'contactName':
        errors.contactName = value.trim() === '' ? 'Full Name is required' : '';
        break;
      case 'title':
        errors.title = value.trim() === '' ? 'Title is required' : '';
        break;
      case 'email':
        errors.email = value.trim() === '' ? 'Email is required' : '';
        // Simple email regex for validation
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value.trim() !== '' && !emailPattern.test(value)) {
          errors.email = 'Invalid email address';
        }
        break;
      case 'phoneNumber':
        errors.phoneNumber = value.trim() === '' ? 'Phone Number is required' : '';
        // Add any phone number validation if necessary
        break;
      case 'agreeToTerms':
        errors.agreeToTerms = value ? '' : 'You must agree to the Terms of Service';
        break;
      default:
        break;
    }

    setFormErrors(errors);
  };

  // Validate all fields on form submission
  const validateAllFields = () => {
    const errors = {};

    if (accountDetails.contactName.trim() === '') {
      errors.contactName = 'Full Name is required';
    }
    if (accountDetails.title.trim() === '') {
      errors.title = 'Title is required';
    }
    if (accountDetails.email.trim() === '') {
      errors.email = 'Email is required';
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(accountDetails.email.trim())) {
        errors.email = 'Invalid email address';
      }
    }
    if (accountDetails.phoneNumber.trim() === '') {
      errors.phoneNumber = 'Phone Number is required';
    }
    if (!accountDetails.newPassword) {
      errors.newPassword = 'Password is required';
    }
    if (!accountDetails.confirmNewPassword) {
      errors.confirmNewPassword = 'Confirm Password is required';
    }
    if (!passwordValidation.isPasswordMatch) {
      errors.confirmNewPassword = 'Passwords do not match';
    }
    if (
      !passwordValidation.hasNumber ||
      !passwordValidation.hasSpecialChar ||
      !passwordValidation.hasUppercase ||
      !passwordValidation.hasLowercase ||
      !passwordValidation.longEnough
    ) {
      errors.newPassword = 'Password does not meet the criteria';
    }
    if (!accountDetails.agreeToTerms) {
      errors.agreeToTerms = 'You must agree to the Terms of Service';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  const handleSubmit = (e) => {
    setSection(3);
    e.preventDefault();
    if (validateAllFields()) {
      setAccountDetailsParent(accountDetails);
      createNewUser(accountDetails);
    }
  };

  return (
    <div className="container px-4 py-b">
      <form noValidate>
        {/* Full Name */}
        <div className="form-group my-b">
          <label className={formTextboxLabel}>
            Full Name <span style={{ color: '#9a016a' }}>*</span>
          </label>
          <input
            type="text"
            value={accountDetails.contactName}
            onChange={handleInputChange}
            className={`form-control ${formErrors.contactName ? 'is-invalid' : ''}`}
            name="contactName"
            placeholder="Full Name"
            required
            style={{ borderRadius: '0' }}
          />
          <div className="invalid-feedback">{formErrors.contactName}</div>
        </div>

        {/* Title */}
        <div className="form-group my-4">
          <label className={formTextboxLabel}>
            Title <span style={{ color: '#9a016a' }}>*</span>
          </label>
          <input
            type="text"
            value={accountDetails.title}
            onChange={handleInputChange}
            className={`form-control ${formErrors.title ? 'is-invalid' : ''}`}
            name="title"
            placeholder="Your role in your orginization"
            required
            style={{ borderRadius: '0' }}
          />
          <div className="invalid-feedback">{formErrors.title}</div>
        </div>

        {/* Email */}
        <div className="form-group my-4">
          <label className={formTextboxLabel}>
            Email <span style={{ color: '#9a016a' }}>*</span>
          </label>
          <input
            type="email"
            value={accountDetails.email}
            onChange={handleInputChange}
            className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
            name="email"
            placeholder="Email"
            required
            style={{ borderRadius: '0' }}
          />
          <div className="invalid-feedback">{formErrors.email}</div>
        </div>

        {/* Phone Number */}
        <div className="form-group my-4">
          <label className={formTextboxLabel}>
            Phone Number <span style={{ color: '#9a016a' }}>*</span>
          </label>
          <PhoneInput
            country={'us'}
            value={accountDetails.phoneNumber}
            onChange={handlePhoneNumberChange}
            inputClass={`form-control ${formErrors.phoneNumber ? 'is-invalid' : ''}`}
            required
            style={{ borderRadius: '0' }}
          />
          {formErrors.phoneNumber && (
            <div className="invalid-feedback d-block">{formErrors.phoneNumber}</div>
          )}
        </div>

        {/* Password */}
        <div className="form-group mt-4 mb-2">
          <label className={formTextboxLabel}>
            Password <span style={{ color: '#9a016a' }}>*</span>
          </label>
          <div className="w-full flex flex-row items-center position-relative">
            <input
              type={isNewPasswordVisible ? 'text' : 'password'}
              className={`form-control ${formErrors.newPassword ? 'is-invalid' : ''}`}
              placeholder="Password"
              value={accountDetails.newPassword}
              onChange={handleNewPasswordChange}
              name="newPassword"
              required
              style={{ borderRadius: '0' }}
            />
            <button
              type="button"
              onClick={toggleNewPasswordVisibility}
              className="position-absolute"
              style={{ right: '10px', background: 'none', border: 'none' }}
            >
              {isNewPasswordVisible ? (
                <PasswordTurnVisibleIcon />
              ) : (
                <PasswordTurnInvisibleIcon />
              )}
            </button>
            <div className="invalid-feedback">{formErrors.newPassword}</div>
          </div>
        </div>

        {/* Password Strength */}
        <div className="w-full flex flex-col gap-[2px]">
          <p className={`font-proximanova text-[15px] mb-0 ${passwordValidation.hasNumber ? '' : 'text-danger'}`}>
            • Contains at least 1 number
          </p>
          <p className={`font-proximanova text-[15px] mb-0 ${passwordValidation.hasSpecialChar ? '' : 'text-danger'}`}>
            • Contains at least 1 special character
          </p>
          <p className={`font-proximanova text-[15px] mb-0 ${passwordValidation.hasUppercase ? '' : 'text-danger'}`}>
            • Contains at least 1 uppercase letter
          </p>
          <p className={`font-proximanova text-[15px] mb-0 ${passwordValidation.hasLowercase ? '' : 'text-danger'}`}>
            • Contains at least 1 lowercase letter
          </p>
          <p className={`font-proximanova text-[15px] mb-0 ${passwordValidation.longEnough ? '' : 'text-danger'}`}>
            • Contains at least 8 characters
          </p>
        </div>

        {/* Confirm New Password */}
        <div className="form-group mt-4 mb-2">
          <label className={formTextboxLabel}>
            Confirm Password <span style={{ color: '#9a016a' }}>*</span>
          </label>
          <div className="w-full flex flex-row items-center position-relative">
            <input
              type={isConfirmPasswordVisible ? 'text' : 'password'}
              className={`form-control ${formErrors.confirmNewPassword ? 'is-invalid' : ''}`}
              placeholder="Confirm Password"
              value={accountDetails.confirmNewPassword}
              onChange={handleConfirmPasswordChange}
              name="confirmNewPassword"
              required
              style={{ borderRadius: '0' }}
            />
            <button
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              className="position-absolute"
              style={{ right: '10px', background: 'none', border: 'none' }}
            >
              {isConfirmPasswordVisible ? (
                <PasswordTurnVisibleIcon />
              ) : (
                <PasswordTurnInvisibleIcon />
              )}
            </button>
            <div className="invalid-feedback">{formErrors.confirmNewPassword}</div>
          </div>
        </div>

        {/* Password Match */}
        <p
          className={`font-proximanova text-[15px] mb-0 ${passwordValidation.isPasswordMatch ? '' : 'text-danger'
            }`}
        >
          {passwordValidation.isPasswordMatch
            ? ''
            : '• Passwords do not match'}
        </p>

        {/* Interested in API */}
        <div className="mt-4 form-group form-check">
          <input
            type="checkbox"
            name="interestedInAPI"
            checked={accountDetails.interestedInAPI}
            onChange={handleInputChange}
            className="form-check-input"
            style={{ borderRadius: '0' }}
          />
          <label className="form-check-label">
            I am interested in the CharmHealth API program
          </label>
        </div>

        {/* Agree to Terms */}
        <div className="form-group mt-2 mb-4">
          <div className="form-check">
            <input
              type="checkbox"
              name="agreeToTerms"
              checked={accountDetails.agreeToTerms}
              onChange={handleInputChange}
              className={`form-check-input ${formErrors.agreeToTerms ? 'is-invalid' : ''}`}
              required
              style={{ borderRadius: '0' }}
            />
            <label className="form-check-label">
              I agree to the{' '}
              <a
                href="https://www.charmhealth.com/ehr/termsofservice.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-charmOrange underline"
              >
                Terms of Service
              </a>
              <span style={{ color: '#9a016a' }}>*</span>
            </label>
            <div className="invalid-feedback">{formErrors.agreeToTerms}</div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-row justify-between pb-8 mt-4">
          <button type="button" className="w-fit text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2" onClick={() => setSection(1)}>
            Back
          </button>
          <button type="button" className="w-fit text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2" onClick={handleSubmit}>
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountForm;